import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { onValue, ref } from 'firebase/database';
import { ToastContainer } from 'react-toastify';
import { FiArrowLeft } from 'react-icons/fi';

import { db } from '../firebase';
import { slugToMac } from '../utils/macSlug';
import HeaderBar from '../components/device-detail/HeaderBar';
import TabStrip, { DEVICE_TABS } from '../components/device-detail/TabStrip';
import KpiStrip from '../components/device-detail/KpiStrip';
import OverviewTab from '../components/device-detail/OverviewTab';
import ControlsTab from '../components/device-detail/ControlsTab';
import HealthTab from '../components/device-detail/HealthTab';
import HardwareTab from '../components/device-detail/HardwareTab';
import AppsTab from '../components/device-detail/AppsTab';
import NetworkTab from '../components/device-detail/NetworkTab';
import BluetoothTab from '../components/device-detail/BluetoothTab';
import LocationTab from '../components/device-detail/LocationTab';
import CommandsTab from '../components/device-detail/CommandsTab';
import LicenseTab from '../components/device-detail/LicenseTab';
import LogsTab from '../components/device-detail/LogsTab';
import NotesTab from '../components/device-detail/NotesTab';
import { getFreshness } from '../utils/deviceData';

// Detail page for a single device, mounted at /devices/:mac.
//
// The :mac URL segment is the underscore form of the MAC (see macSlug.js).
// We convert to the colon form before any RTDB lookup, since the RTDB key
// for `mdm/devices/<key>` is stored with colons.
//
// Active tab is reflected in the URL hash (e.g. /devices/AA_.../#health) so
// links and refreshes preserve the user's place.

export default function DeviceDetailPage() {
    const { mac: slug } = useParams();
    const mac = slugToMac(slug);
    const navigate = useNavigate();
    const location = useLocation();

    // ---- Live device data ----------------------------------------------
    // Subscribe to mdm/devices/<MAC> for the lifetime of the page. RTDB
    // pushes us the initial value once and then any subsequent change.
    const [device, setDevice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!mac) return undefined;
        const deviceRef = ref(db, `mdm/devices/${mac}`);
        const unsubscribe = onValue(
            deviceRef,
            (snap) => {
                const val = snap.val();
                setDevice(val ?? null);
                setNotFound(val == null);
                setLoading(false);
            },
            (err) => {
                // Auth/rules failure or network drop. Surface as not-found
                // for now; we can refine error UX later.
                console.error('[DeviceDetailPage] subscription error', err);
                setLoading(false);
                setNotFound(true);
            }
        );
        return () => unsubscribe();
    }, [mac]);

    // ---- Lookup tables (small shared dictionaries) ---------------------
    // These power country names + group labels in the Overview tab. They're
    // tiny and shared across pages, so a separate subscription is cheap.
    const [ipToCountry, setIpToCountry] = useState({});
    const [groupList, setGroupList] = useState({});
    const [apps, setApps] = useState({});

    useEffect(() => {
        const ipRef = ref(db, 'mdm/ip_to_country');
        const groupsRef = ref(db, 'mdm/groups');
        // App-store entries live at mdm/app_store (dots → underscores in
        // the key). DevicesPage subscribes to the same path; we mirror it
        // here so the Apps tab's "Update available" check has data.
        const appsRef = ref(db, 'mdm/app_store');
        const unsubIp = onValue(ipRef, (snap) => setIpToCountry(snap.val() || {}));
        const unsubGroups = onValue(groupsRef, (snap) => setGroupList(snap.val() || {}));
        const unsubApps = onValue(appsRef, (snap) => setApps(snap.val() || {}));
        return () => {
            unsubIp();
            unsubGroups();
            unsubApps();
        };
    }, []);

    // ---- Document title -------------------------------------------------
    // Keep the browser tab title meaningful so a stack of MDM tabs is
    // legible from the OS task switcher. Reset on unmount.
    useEffect(() => {
        const previous = document.title;
        const friendly = device?.name?.trim() || mac || 'Device';
        document.title = `${friendly} · MDM`;
        return () => { document.title = previous; };
    }, [device?.name, mac]);

    // ---- Tab routing via hash ------------------------------------------
    // location.hash includes the leading "#"; strip it. Default to overview
    // when the URL has no hash or an unknown one.
    const activeTab = useMemo(() => {
        const fromHash = (location.hash || '').replace(/^#/, '');
        const known = DEVICE_TABS.some((t) => t.id === fromHash);
        return known ? fromHash : 'overview';
    }, [location.hash]);

    const onTabChange = (tabId) => {
        // `replace: true` keeps tab clicks out of the browser history stack —
        // back-button should return to /devices, not cycle through tabs.
        navigate(`#${tabId}`, { replace: true });
    };

    // ---- Keyboard shortcuts --------------------------------------------
    // Esc returns to the device list; ←/→ cycles tabs. We ignore key
    // events that bubble up from form fields so typing in inputs / notes
    // doesn't trigger navigation.
    useEffect(() => {
        const handler = (e) => {
            const inField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName);
            if (e.key === 'Escape' && !inField) {
                navigate('/devices');
                return;
            }
            if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !inField) {
                const idx = DEVICE_TABS.findIndex((t) => t.id === activeTab);
                if (idx === -1) return;
                const nextIdx = e.key === 'ArrowRight'
                    ? (idx + 1) % DEVICE_TABS.length
                    : (idx - 1 + DEVICE_TABS.length) % DEVICE_TABS.length;
                onTabChange(DEVICE_TABS[nextIdx].id);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [activeTab, navigate]);

    // ---- Render --------------------------------------------------------
    if (loading) {
        return <CenteredMessage>Loading device…</CenteredMessage>;
    }
    if (notFound) {
        return (
            <CenteredMessage>
                <p className="mb-4">Device <span className="font-mono text-gray-300">{mac}</span> not found.</p>
                <button
                    onClick={() => navigate('/devices')}
                    className="btn-secondary"
                >
                    <FiArrowLeft /> Back to Devices
                </button>
            </CenteredMessage>
        );
    }

    return (
        // Negative margins cancel MainLayout's `px-6 py-8` so the
        // surface-page background bleeds edge-to-edge past the slate-blue
        // gradient that MainLayout sets on the whole app shell. We don't
        // re-add inner padding here because HeaderBar / TabStrip want to
        // sit flush with the top edge.
        <div className="min-h-screen text-white -mx-6 -my-8 bg-surface-page">
            {/* Local container so toasts triggered on this page are actually
                visible. The other pages each ship their own — react-toastify
                only renders into a mounted container, so without one here
                the toasts queue up until the user navigates away. */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />

            <HeaderBar mac={mac} device={device} />
            <TabStrip activeTab={activeTab} onTabChange={onTabChange} />

            <div className="px-6 py-6 space-y-6">
                <StaleBanner device={device} />
                <KpiStrip device={device} mac={mac} />

                {activeTab === 'overview' && (
                    <OverviewTab
                        mac={mac}
                        device={device}
                        ipToCountry={ipToCountry}
                        groupList={groupList}
                    />
                )}
                {activeTab === 'controls' && <ControlsTab mac={mac} device={device} />}
                {activeTab === 'health' && <HealthTab device={device} />}
                {activeTab === 'hardware' && <HardwareTab mac={mac} device={device} />}
                {activeTab === 'apps' && <AppsTab mac={mac} device={device} apps={apps} />}
                {activeTab === 'network' && <NetworkTab mac={mac} device={device} ipToCountry={ipToCountry} />}
                {activeTab === 'bluetooth' && <BluetoothTab mac={mac} device={device} />}
                {activeTab === 'location' && <LocationTab mac={mac} device={device} />}
                {activeTab === 'commands' && <CommandsTab mac={mac} device={device} />}
                {activeTab === 'license' && <LicenseTab mac={mac} device={device} />}
                {activeTab === 'logs' && <LogsTab mac={mac} />}
                {activeTab === 'notes' && <NotesTab mac={mac} />}
            </div>
        </div>
    );
}

function CenteredMessage({ children }) {
    return (
        <div className="min-h-screen -mx-6 -my-8 bg-surface-page text-white flex items-center justify-center">
            <div className="text-center">{children}</div>
        </div>
    );
}

// Yellow banner shown when device_data is older than 30 minutes. Numbers
// further down the page may not reflect current device state.
function StaleBanner({ device }) {
    const fresh = getFreshness(device?.device_data?.updated_at);
    if (!fresh.isStale) return null;
    return (
        <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-4 py-3 text-sm text-yellow-300">
            ⚠️ Telemetry is <strong>stale</strong> — last update was {fresh.label}. Metrics
            below may not reflect the device's current state.
        </div>
    );
}
